/**
 * 提现渠道公共工具（2026-06-08）
 *
 * 统一自提现 v2 流程中：渠道判断、是否已绑定、绑定/提现参数字段、确认页展示行。
 * 字段规则对齐旧页 withdrawalDetails / indonesia / pixDetails 的 submitBtn 逻辑。
 *
 * 流程：
 *   initForWeb → isChannelBound ?
 *     否 → channel-bind-sheet + bindInfo/save（buildBindParams）
 *     是 → self-withdraw-confirm + applyForWeb（buildApplyParams）
 */

/** PIX 巴西渠道：支付类型 9（CPF）与 10（账户） */
export const PIX_CHANNEL_IDS = [9, 10];
/** 印尼电子钱包 OVO/DANA/GOPAY/LINKAJA/SHOPEEPAY 等 */
export const INDONESIA_WALLET_IDS = [18, 19, 20, 21, 22];
/** 印尼 MP_BANK_TRANSFER（银行转账）渠道 id */
export const INDONESIA_BANK_ID = 15;
/** 印尼 MP_E_WALLET（电子钱包聚合）渠道 id */
export const INDONESIA_EWALLET_ID = 16;
/** 全部印尼渠道：银行 + 钱包聚合 + 各钱包 */
const INDONESIA_IDS = [15, 16, 18, 19, 20, 21, 22];

// 是否 PIX 巴西渠道（id 为 9 或 10）
export function isPixChannel(itemId) {
  return PIX_CHANNEL_IDS.includes(Number(itemId));
}

// 是否任意印尼渠道（银行 / 钱包聚合 / 电子钱包）
export function isIndonesiaChannel(itemId) {
  return INDONESIA_IDS.includes(Number(itemId));
}

// 是否印尼电子钱包渠道（OVO/DANA/GOPAY/LINKAJA/SHOPEEPAY）
export function isIndonesiaWalletChannel(itemId) {
  return INDONESIA_WALLET_IDS.includes(Number(itemId));
}

// 是否印尼银行转账渠道（id === 15）
export function isIndonesiaBankChannel(itemId) {
  return Number(itemId) === INDONESIA_BANK_ID;
}

// 是否印尼电子钱包聚合渠道（id === 16）
export function isIndonesiaEwalletChannel(itemId) {
  return Number(itemId) === INDONESIA_EWALLET_ID;
}

/** GPay 渠道：id === 11 */
export function isGpayChannel(itemId) {
  return Number(itemId) === 11;
}

/** 土耳其渠道：id === 4 */
export function isTrChannel(itemId) {
  return Number(itemId) === 4;
}

/** 读取 localStorage，兼容旧页缓存字段（num{id}、id、fullName 等） */
function readLs(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
}

/**
 * 判断渠道是否已绑定收款信息
 * 优先读接口 infos.bound/hasBind/bindStatus，再按渠道字段兜底，最后兼容旧 localStorage
 */
export function isChannelBound(infos = {}, itemId) {
  if (!infos) return false;
  // 接口显式标记已绑定：bound / hasBind / bindStatus===1
  if (infos.bound === true || infos.hasBind === true || infos.bindStatus === 1) {
    return true;
  }
  const id = Number(itemId);

  // GPay：只要有账户名或 IBAN 即认为已绑定
  if (isGpayChannel(id)) {
    return !!(infos.savedAccountName || infos.accountName || infos.savedIban || infos.iban);
  }
  // 土耳其：收款人姓名与账号都齐全才认为已绑定
  if (isTrChannel(id)) {
    return !!(
      (infos.savedPayeeName || infos.payeeName) &&
      (infos.savedPayeeAccount || infos.payeeAccount)
    );
  }
  // PIX：姓名 + 证件号 + 账号 + WhatsApp 四项齐全
  if (isPixChannel(id)) {
    const name = infos.savedName || infos.name || readLs("name");
    const docId = infos.savedDocumentId || infos.document_id || readLs("document_id");
    const account = infos.savedAccount || infos.account || readLs("account");
    const whatsApp = infos.savedWhatsApp || infos.whatsApp || readLs("id");
    return !!(name && docId && account && whatsApp);
  }
  // 印尼电子钱包：姓名 + 账号 + WhatsApp 齐全
  if (isIndonesiaWalletChannel(id)) {
    const payeeName =
      infos.savedPayeeName || infos.payeeName || infos.savedFullName || infos.fullName || readLs("fullName");
    const payeeAccount = infos.savedPayeeAccount || infos.payeeAccount || readLs("payeeAccount");
    const whatsApp = infos.savedWhatsApp || infos.whatsApp || readLs("whatsApp");
    return !!(payeeName && payeeAccount && whatsApp);
  }
  // 印尼银行转账：姓名 + 银行编码 + 卡号 + WhatsApp 齐全
  if (isIndonesiaBankChannel(id)) {
    const fullName = infos.savedFullName || infos.fullName || infos.account || readLs("fullName");
    const bankId = infos.savedBankId || infos.bankId || infos.account_bank_code;
    const cardNumber = infos.savedCardNumber || infos.cardNumber || infos.account_number || readLs("cardNumber");
    const whatsApp = infos.savedWhatsApp || infos.whatsApp || readLs("whatsApp");
    return !!(fullName && bankId && cardNumber && whatsApp);
  }
  // 印尼电子钱包聚合：姓名 + 银行编码 + 手机号 + WhatsApp 齐全
  if (isIndonesiaEwalletChannel(id)) {
    const fullName = infos.savedFullName || infos.fullName || infos.account || readLs("fullName");
    const bankId = infos.savedBankId || infos.bankId || infos.account_bank_code;
    const phone = infos.savedPhoneNumber || infos.phoneNumber || infos.account_phone || readLs("phoneNumber");
    const whatsApp = infos.savedWhatsApp || infos.whatsApp || readLs("whatsApp");
    return !!(fullName && bankId && phone && whatsApp);
  }

  // 通用渠道兜底：账号 + WhatsApp 都有即已绑定
  const account = infos.savedPayeeAccount || infos.payeeAccount || infos.account;
  const whatsApp = infos.savedWhatsApp || infos.whatsApp;
  if (account && whatsApp) return true;
  // 旧 withdrawalDetails 缓存：num{渠道id} + id(WhatsApp)
  const legacyNum = readLs("num" + id);
  const legacyId = readLs("id");
  return !!(legacyNum && legacyId);
}

/** 确认页预计到账时间文案；优先接口 arrivalTimeDesc */
// 确认页预计到账时间文案；依次取接口字段，缺省为空
export function getArrivalTimeText(infos = {}) {
  return (
    infos.arrivalTimeDesc ||
    infos.estimatedArrivalTime ||
    infos.arrivalTime ||
    ""
  );
}

// 是否需要展示手续费：showFee 为 true 或 1
export function shouldShowFee(infos = {}) {
  return infos.showFee === true || infos.showFee === 1;
}

// 是否需要展示实际到账金额：showActualAmount 为 true 或 1
export function shouldShowActualAmount(infos = {}) {
  return infos.showActualAmount === true || infos.showActualAmount === 1;
}

/**
 * 绑定半层 bindInfo/save 的 params
 * 不含 amount/pocketCoin，字段与旧页表单一致
 */
export function buildBindParams(itemId, form = {}) {
  const id = Number(itemId);

  // GPay：iban 去除所有空白字符，支行/账号缺省补 "111111"
  if (isGpayChannel(id)) {
    return {
      whatsApp: form.whatsApp,
      accountName: form.accountName,
      bankId: form.bankId,
      iban: (form.iban || "").replace(/\s+/g, ""),
      branchNumber: form.branchNumber || "111111",
      accountNumber: form.accountNumber || "111111",
      type: form.type || "ACCOUNT",
    };
  }
  // 土耳其：收款账号 + 姓名 + WhatsApp
  if (isTrChannel(id)) {
    return {
      payeeAccount: form.payeeAccount,
      payeeName: form.payeeName,
      whatsApp: form.whatsApp,
    };
  }
  // PIX：CPF/账户模式字段
  if (isPixChannel(id)) {
    return {
      name: form.name,
      whatsApp: form.whatsApp,
      document_type: form.document_type,
      document_id: form.document_id,
      account_type: form.account_type,
      account: form.account,
    };
  }
  // 印尼电子钱包：姓名映射到 payeeName
  if (isIndonesiaWalletChannel(id)) {
    return {
      payeeName: form.fullName,
      payeeAccount: form.payeeAccount,
      whatsApp: form.whatsApp,
    };
  }
  // 印尼银行转账：账户映射到 account/account_bank_code/account_number
  if (isIndonesiaBankChannel(id)) {
    return {
      account: form.fullName,
      account_bank_code: form.bankId,
      account_number: form.cardNumber,
      whatsApp: form.whatsApp,
    };
  }
  // 印尼电子钱包聚合：手机号映射到 account_phone
  if (isIndonesiaEwalletChannel(id)) {
    return {
      account: form.fullName,
      account_bank_code: form.bankId,
      account_phone: form.phoneNumber,
      whatsApp: form.whatsApp,
    };
  }
  // 5 Vodafone / 17 Dreamo：旧页用 account 字段
  if (id === 5 || id === 17) {
    return {
      account: form.account,
      whatsApp: form.whatsApp,
      payeeName: form.payeeName,
    };
  }
  // 其余通用渠道：account 作为 payeeAccount 提交
  return {
    payeeAccount: form.account,
    whatsApp: form.whatsApp,
    payeeName: form.payeeName,
  };
}

/**
 * 确认页 applyForWeb 的 params
 * 对齐旧页 withdrawalDetails.submitBtn / indonesia.gPaySubmit / pixDetails.submitBtn
 * 印尼渠道需带 amount（法币到账金额 detailsNewObj.num）
 */
export function buildApplyParams(itemId, detailsObj = {}, detailsNewObj = {}) {
  const id = Number(itemId);
  // 统一取 WhatsApp，兼容接口字段与旧 localStorage
  const whatsApp =
    detailsObj.whatsApp ||
    detailsObj.displayWhatsApp ||
    readLs("id") ||
    readLs("whatsApp");

  // GPay：iban 去空白，支行/账号/类型走固定值
  if (isGpayChannel(id)) {
    return {
      whatsApp,
      accountName: detailsObj.accountName || detailsObj.savedAccountName,
      bankId: detailsObj.bankId || detailsObj.savedBankId,
      iban: (detailsObj.iban || detailsObj.savedIban || "").replace(/\s+/g, ""),
      branchNumber: "111111",
      accountNumber: "111111",
      type: "ACCOUNT",
    };
  }
  // 土耳其：收款账号 + 姓名 + WhatsApp
  if (isTrChannel(id)) {
    return {
      payeeAccount: detailsObj.payeeAccount || detailsObj.savedPayeeAccount,
      payeeName: detailsObj.payeeName || detailsObj.savedPayeeName,
      whatsApp,
    };
  }
  // PIX：num 从旧缓存取，证件/账户类型缺省 "CPF"
  if (isPixChannel(id)) {
    return {
      num: readLs("num") || "",
      name: detailsObj.name || detailsObj.savedName || readLs("name"),
      whatsApp,
      document_type: detailsObj.document_type || detailsObj.savedDocumentType || readLs("document_type") || "CPF",
      document_id: detailsObj.document_id || detailsObj.savedDocumentId || readLs("document_id"),
      account_type: detailsObj.account_type || detailsObj.savedAccountType || readLs("account_type") || "CPF",
      account: detailsObj.account || detailsObj.savedAccount || readLs("account"),
    };
  }
  // 印尼电子钱包：需带 amount（法币到账金额 detailsNewObj.num）
  if (isIndonesiaWalletChannel(id)) {
    return {
      amount: detailsNewObj.num,
      payeeName:
        detailsObj.payeeName ||
        detailsObj.fullName ||
        detailsObj.savedPayeeName ||
        readLs("fullName"),
      payeeAccount:
        detailsObj.payeeAccount || detailsObj.savedPayeeAccount || readLs("payeeAccount"),
      whatsApp: detailsObj.whatsApp || readLs("whatsApp"),
    };
  }
  // 印尼银行转账：amount + account + bank_code + 卡号
  if (isIndonesiaBankChannel(id)) {
    return {
      amount: detailsNewObj.num,
      account: detailsObj.fullName || detailsObj.account || readLs("fullName"),
      account_bank_code: detailsObj.bankId || detailsObj.account_bank_code || detailsObj.savedBankId,
      account_number:
        detailsObj.cardNumber || detailsObj.account_number || readLs("cardNumber"),
      whatsApp: detailsObj.whatsApp || readLs("whatsApp"),
    };
  }
  // 印尼电子钱包聚合：amount + account + bank_code + 手机号
  if (isIndonesiaEwalletChannel(id)) {
    return {
      amount: detailsNewObj.num,
      account: detailsObj.fullName || detailsObj.account || readLs("fullName"),
      account_bank_code: detailsObj.bankId || detailsObj.account_bank_code || detailsObj.savedBankId,
      account_phone:
        detailsObj.phoneNumber || detailsObj.account_phone || readLs("phoneNumber"),
      whatsApp: detailsObj.whatsApp || readLs("whatsApp"),
    };
  }

  // 通用渠道账号兜底：优先接口字段，再取旧缓存 num{id}
  const account =
    detailsObj.payeeAccount ||
    detailsObj.account ||
    readLs("num" + id);
  // 5/17 旧页 params 仅 account + whatsApp
  if (id === 5 || id === 17) {
    return { account, whatsApp };
  }
  return { payeeAccount: account, whatsApp };
}

/** 确认页收款信息展示行；后端 confirmFields 优先；labels 为 i18n label 兜底 */
export function buildConfirmRows(infos = {}, itemId, labels = {}) {
  // 后端已下发 confirmFields 时直接使用（label/value 结构）
  if (Array.isArray(infos.confirmFields) && infos.confirmFields.length) {
    return infos.confirmFields.map((row) => ({
      label: row.label || row.key,
      value: row.value || "",
    }));
  }
  const rows = [];
  // 收集器：label 与 value 都非空才入行（label 为空时用 i18n 兜底）
  const push = (label, value, labelKey) => {
    if (value) rows.push({ label: label || labels[labelKey] || "", value });
  };
  const id = Number(itemId);

  // GPay：姓名 / 银行 / IBAN / WhatsApp 四行
  if (isGpayChannel(id)) {
    push(infos.userFullName, infos.displayAccountName || infos.accountName, "name");
    push(infos.bank, infos.displayBankName || infos.bankName, "bank");
    push(infos.iban, infos.displayIban || infos.iban, "iban");
    push(infos.whatsAppMessage, infos.displayWhatsApp || infos.whatsApp, "whatsApp");
    return rows;
  }
  // 土耳其：收款人姓名 / 账号 / WhatsApp
  if (isTrChannel(id)) {
    push(infos.pmnameMessage, infos.displayPayeeName || infos.payeeName, "name");
    push(infos.pmaccountMessage, infos.displayPayeeAccount || infos.payeeAccount, "account");
    push(infos.whatsAppMessage, infos.displayWhatsApp || infos.whatsApp, "whatsApp");
    return rows;
  }
  // PIX：姓名 / 证件类型 / 证件号 / 账户类型 / 账号 / WhatsApp，过滤空值行
  if (isPixChannel(id)) {
    push(infos.nameLabel || "Name", infos.displayName || infos.name, "name");
    push(infos.documentTypeLabel || "Document Type", infos.document_type, "documentType");
    push(infos.documentIdLabel || "Document ID", infos.document_id, "documentId");
    push(infos.accountTypeLabel || "Account Type", infos.account_type, "accountType");
    push(infos.accountIdLabel || "Account ID", infos.account, "account");
    push(infos.whatsAppMessage, infos.displayWhatsApp || infos.whatsApp, "whatsApp");
    return rows.filter((r) => r.value);
  }
  // 印尼电子钱包：姓名 / 账号 / WhatsApp，过滤空值行
  if (isIndonesiaWalletChannel(id)) {
    push(infos.pmnameTip || infos.pmnameMessage, infos.displayPayeeName || infos.payeeName || infos.fullName, "name");
    push(infos.pmaccountTip || infos.pmaccountMessage, infos.displayPayeeAccount || infos.payeeAccount, "account");
    push(infos.whatsAppMessage, infos.displayWhatsApp || infos.whatsApp, "whatsApp");
    return rows.filter((r) => r.value);
  }
  // 印尼银行 / 电子钱包聚合：姓名 / 银行 / 账号(卡号或手机号) / WhatsApp
  if (isIndonesiaBankChannel(id) || isIndonesiaEwalletChannel(id)) {
    push(infos.pnidMessage, infos.displayName || infos.fullName || infos.account, "name");
    push(infos.bank, infos.displayBankName || infos.bankName, "bank");
    push(
      infos.bankCardNumber,
      infos.displayPayeeAccount ||
        infos.cardNumber ||
        infos.phoneNumber ||
        infos.account_number ||
        infos.account_phone,
      "account"
    );
    push(infos.whatsAppMessage, infos.displayWhatsApp || infos.whatsApp, "whatsApp");
    return rows.filter((r) => r.value);
  }

  // 其他通用渠道：姓名 / 国家 / 银行 / 账号 / 地址 / WhatsApp，过滤空值行
  push(infos.pmnameMessage || infos.pnidMessage, infos.displayName || infos.fullName || infos.payeeName, "name");
  push(infos.countryMessage || "国家/地区", infos.country || infos.displayCountry, "country");
  push(infos.bank, infos.displayBankName || infos.bankName, "bank");
  push(
    infos.bankCardNumber || infos.pnidMessage || infos.pmaccountMessage,
    infos.displayPayeeAccount || infos.payeeAccount || infos.account,
    "account"
  );
  push(infos.addressMessage || "地址(英文)", infos.address || infos.displayAddress, "address");
  push(infos.whatsAppMessage, infos.displayWhatsApp || infos.whatsApp, "whatsApp");
  return rows.filter((r) => r.value);
}
